import { useEffect, useRef, useState, type RefObject } from 'react';
import { formatMoney } from '../../utils/costFormatting';
import { calculateCartPreview, cartItemCount, formatProductTaxLabel, type CartLine, type CartPreview } from '../../utils/otherSalePos';
import { describeUnits } from '../../utils/posView';
import { Icon } from '../ui/Icon';
import { Button } from '../ui/primitives';
import { cx } from '../ui/primitives/cx';
import { PosStepper } from './PosParts';
import { scrollParentOf } from './scrollParent';

// Rediseño · Bloque D — el pedido (P-025): la barra de abajo, sus líneas y su pie. Solo dibuja.
//
// Lo que la pantalla aprobada trae y aquí NO está, porque el producto no lo tiene: «Cliente»,
// «Descuento» y «Nota»; el renglón «Descuento»; «Guardar» (lo que guarda la venta es «Cobrar»); y la
// miniatura de cada línea. El renglón de IVA sí está: es un dato real del pedido.

const money = (value: number) => formatMoney(value.toFixed(2));

/**
 * La barra del pedido, pegada abajo: cuántos artículos van, «Ver pedido» y «Cobrar $total».
 * Vacía no es un botón: dice qué hacer para empezar.
 *
 * P-080 (D-P080-DIRECT-CHARGE): se cobra desde aquí, sin abrir el pedido. «Cobrar» es el MISMO de la
 * hoja —quien usa esto le pasa la misma función, el mismo apagado y la misma espera— y el total es el
 * mismo estimado del pedido; el que se cobra lo fija el servidor y se ve en la pantalla de cobro antes
 * de confirmar. «Ver pedido» sigue ahí para quien quiere revisar.
 */
export function PosOrderBar({ cart, total, saving, disabled, onOpen, onCheckout }: {
  cart: CartLine[];
  total: number;
  /** Se está guardando la venta («Guardando…»). */
  saving: boolean;
  /** Lo mismo que apaga «Cobrar» en la hoja. */
  disabled: boolean;
  onOpen: () => void;
  onCheckout: () => void;
}) {
  if (cart.length === 0) {
    return (
      <div className="n3-pos__bar n3-pos__bar--empty">
        <p className="n3-pos__bar-hint">Toca un producto para empezar</p>
      </div>
    );
  }
  return (
    <div className="n3-pos__bar">
      <div className="n3-pos__bar-row">
        {/* Abrir el pedido siempre se puede: no toca nada (y con una venta guardándose, la hoja la espera). */}
        <button className="n3-pos__bar-button" onClick={onOpen} type="button">
          <span className="n3-pos__bar-units">{describeUnits(cartItemCount(cart))}</span>
          <span className="n3-pos__bar-go">
            <span>Ver pedido</span>
            <Icon name="chevron-up" size={18} />
          </span>
        </button>
        <Button className="n3-pos__bar-cta" disabled={disabled} loading={saving} loadingLabel="Guardando…" onClick={onCheckout} size="lg" variant="primary">
          {`Cobrar ${money(total)}`}
        </Button>
      </div>
    </div>
  );
}

/**
 * Cuántas líneas quedan fuera de la vista, arriba y abajo (P-025: «3 más»). Es una pista, no un
 * dato: si no se puede medir —no hay maquetación, o nada se desplaza— no se dibuja nada. Quien se
 * desplaza es el cuerpo de la hoja, o el del panel cuando el pedido va al lado del catálogo.
 */
function useHiddenLines(list: RefObject<HTMLUListElement>, count: number): { above: number; below: number } {
  const [hidden, setHidden] = useState({ above: 0, below: 0 });
  useEffect(() => {
    const element = list.current;
    const scroller = element ? scrollParentOf(element) : null;
    if (!element || !scroller) return undefined;
    const measure = () => {
      const view = scroller.getBoundingClientRect();
      let above = 0;
      let below = 0;
      // Una línea cuenta como oculta cuando más de la mitad queda fuera.
      for (const line of Array.from(element.children)) {
        const box = line.getBoundingClientRect();
        const middle = box.top + box.height / 2;
        if (middle < view.top) above += 1;
        else if (middle > view.bottom) below += 1;
      }
      setHidden((current) => (current.above === above && current.below === below ? current : { above, below }));
    };
    measure();
    scroller.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
    observer?.observe(scroller);
    observer?.observe(element);
    return () => {
      scroller.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [list, count]);
  return hidden;
}

/**
 * Las líneas del pedido. «Vaciar» va arriba, junto al conteo, donde lo pone la pantalla aprobada;
 * la confirmación la pide quien usa este componente.
 */
export function PosOrderLines({ cart, busy, onDecrease, onIncrease, onRemove, onClear }: {
  cart: CartLine[];
  busy: boolean;
  onDecrease: (productId: string) => void;
  onIncrease: (productId: string) => void;
  onRemove: (productId: string) => void;
  onClear: () => void;
}) {
  const list = useRef<HTMLUListElement>(null);
  const empty = useRef<HTMLDivElement>(null);
  const hidden = useHiddenLines(list, cart.length);

  // Quitar una línea, o vaciar el pedido, borra el botón que tenía el foco. Sin esto el foco caería al
  // fondo de la página con la hoja todavía abierta, y un lector de pantalla se iría al principio del
  // documento. Pasa a la línea que ocupa ese lugar, o a «Tu pedido está vacío». Solo cuando el foco se
  // perdió de verdad: si está en otro sitio, se respeta.
  const ids = cart.map((line) => line.productId);
  const shownIds = useRef(ids);
  const key = ids.join('|');
  useEffect(() => {
    const before = shownIds.current;
    shownIds.current = ids;
    const lost = document.activeElement === null || document.activeElement === document.body;
    if (!lost || before.length <= ids.length) return;
    if (ids.length === 0) {
      empty.current?.focus();
      return;
    }
    const gone = Math.max(before.findIndex((id) => !ids.includes(id)), 0);
    list.current?.children[Math.min(gone, ids.length - 1)]?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
    // `ids` cambia de identidad en cada render; lo que cuenta es su contenido (`key`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (cart.length === 0) {
    return (
      <div className="n3-pos__order-empty" ref={empty} role="status" tabIndex={-1}>
        <span aria-hidden="true" className="n3-pos__order-empty-icon"><Icon name="sell" size={40} /></span>
        <strong className="n3-pos__order-empty-title">Tu pedido está vacío</strong>
        <span>Toca un producto para agregarlo.</span>
      </div>
    );
  }

  return (
    <div className="n3-pos__order">
      <div className="n3-pos__order-meta">
        <p className="n3-pos__order-units">{describeUnits(cartItemCount(cart))}</p>
        <Button className="n3-pos__order-clear" disabled={busy} onClick={onClear} variant="danger">Vaciar</Button>
      </div>
      <div aria-hidden="true" className={cx('n3-pos__more', 'n3-pos__more--above', hidden.above > 0 && 'n3-pos__more--on')}>
        <span className="n3-pos__more-fade" />
        <span className="n3-pos__more-pill"><Icon name="arrow-up" size={14} /><span>{hidden.above} más</span></span>
      </div>
      <ul aria-label="Productos del pedido" className="n3-pos__lines" ref={list}>
        {cart.map((line) => (
          <li className="n3-pos__line" key={line.productId}>
            <div className="n3-pos__line-text">
              <strong className="n3-pos__line-name">{line.name}</strong>
              <span className="n3-pos__line-unit">{formatMoney(line.unitPrice)} c/u · {formatProductTaxLabel(line)}</span>
              <span className="n3-pos__line-total">{money(calculateCartPreview([line]).totalAmount)}</span>
            </div>
            <PosStepper
              disabled={busy}
              name={line.name}
              onDecrease={() => onDecrease(line.productId)}
              onIncrease={() => onIncrease(line.productId)}
              onRemove={() => onRemove(line.productId)}
              quantity={line.quantity}
            />
          </li>
        ))}
      </ul>
      <div aria-hidden="true" className={cx('n3-pos__more', 'n3-pos__more--below', hidden.below > 0 && 'n3-pos__more--on')}>
        <span className="n3-pos__more-fade" />
        <span className="n3-pos__more-pill"><Icon name="arrow-down" size={14} /><span>{hidden.below} más</span></span>
      </div>
    </div>
  );
}

/** Los importes del pedido son ESTIMADOS —los calcula la pantalla con el precio que cargó— y se dice. */
export function PosOrderEstimate() {
  return <p className="n3-pos__estimate">Importes estimados. El servidor confirma el total final.</p>;
}

/**
 * El pie del pedido: los importes y «Cobrar». El total que se cobra es el que fija el servidor al
 * guardar la venta. En la hoja, la frase que lo dice va al final de las líneas (`PosOrderEstimate`):
 * el pie se queda con lo que hay que ver siempre.
 */
export function PosOrderFoot({ preview, saving, disabled, sheet, onCheckout }: {
  preview: CartPreview;
  /** Se está guardando la venta («Guardando…»). */
  saving: boolean;
  disabled: boolean;
  /** Va al pie de la hoja del pedido: en una pantalla baja se acomoda para dejarle sitio a las líneas. */
  sheet: boolean;
  onCheckout: () => void;
}) {
  return (
    <div className={cx('n3-pos__order-foot', sheet && 'n3-pos__order-foot--sheet')}>
      <dl className="n3-pos__totals">
        <div className="n3-pos__totals-row"><dt className="n3-pos__totals-label">Subtotal</dt><dd className="n3-pos__totals-value">{money(preview.subtotalAmount)}</dd></div>
        <div className="n3-pos__totals-row"><dt className="n3-pos__totals-label">IVA</dt><dd className="n3-pos__totals-value">{money(preview.taxAmount)}</dd></div>
        <div className="n3-pos__totals-row n3-pos__totals-row--total"><dt className="n3-pos__totals-label">Total</dt><dd className="n3-pos__totals-value">{money(preview.totalAmount)}</dd></div>
      </dl>
      {!sheet && <PosOrderEstimate />}
      <Button className="n3-pos__cta" disabled={disabled} fullWidth loading={saving} loadingLabel="Guardando…" onClick={onCheckout} size="lg" variant="primary">
        {`Cobrar ${money(preview.totalAmount)}`}
      </Button>
    </div>
  );
}
